/**
 * Financial System Type Definitions
 * Clean, properly typed interfaces for the rebuilt financial system
 */

// ============================================================================
// ENUMS
// ============================================================================

export enum AccountType {
  ASSET = 'asset',
  LIABILITY = 'liability',
  EQUITY = 'equity',
  REVENUE = 'revenue',
  EXPENSE = 'expense',
}

export enum AccountSubtype {
  // Assets
  CASH = 'cash',
  ACCOUNTS_RECEIVABLE = 'accounts_receivable',
  INVENTORY = 'inventory',
  PREPAID_EXPENSES = 'prepaid_expenses',
  FIXED_ASSETS = 'fixed_assets',
  ACCUMULATED_DEPRECIATION = 'accumulated_depreciation',

  // Liabilities
  ACCOUNTS_PAYABLE = 'accounts_payable',
  ACCRUED_EXPENSES = 'accrued_expenses',
  UNEARNED_REVENUE = 'unearned_revenue',
  LOANS_PAYABLE = 'loans_payable',

  // Equity
  NET_ASSETS_UNRESTRICTED = 'net_assets_unrestricted',
  NET_ASSETS_TEMP_RESTRICTED = 'net_assets_temp_restricted',
  NET_ASSETS_PERM_RESTRICTED = 'net_assets_perm_restricted',
  RETAINED_EARNINGS = 'retained_earnings',

  // Revenue
  CONTRIBUTIONS = 'contributions',
  GRANTS = 'grants',
  PROGRAM_REVENUE = 'program_revenue',
  INVESTMENT_INCOME = 'investment_income',

  // Expenses
  PROGRAM_EXPENSES = 'program_expenses',
  PERSONNEL_EXPENSES = 'personnel_expenses',
  ADMINISTRATIVE_EXPENSES = 'administrative_expenses',
  FACILITY_EXPENSES = 'facility_expenses',
  FUNDRAISING_EXPENSES = 'fundraising_expenses',
}

export enum ExpenseStatus {
  DRAFT = 'draft',
  PENDING_APPROVAL = 'pending_approval',
  APPROVED = 'approved',
  PARTIALLY_PAID = 'partially_paid',
  PAID = 'paid',
  REJECTED = 'rejected',
  CANCELLED = 'cancelled',
}

export enum JournalStatus {
  DRAFT = 'draft',
  POSTED = 'posted',
  VOID = 'void',
}

export enum SourceType {
  MANUAL = 'manual',
  EXPENSE = 'expense',
  CONTRIBUTION = 'contribution',
  DIGITAL_PAYMENT = 'digital_payment',
  PLEDGE_PAYMENT = 'pledge_payment',
  DEPRECIATION = 'depreciation',
  BANK_RECONCILIATION = 'bank_reconciliation',
  YEAR_END_CLOSING = 'year_end_closing',
}

export enum ContributionType {
  TITHE = 'tithe',
  OFFERING = 'offering',
  DONATION = 'donation',
  PLEDGE = 'pledge',
  BUILDING_FUND = 'building_fund',
  MISSIONS = 'missions',
  SPECIAL_OFFERING = 'special_offering',
}

export enum ContributionMethod {
  CASH = 'cash',
  CHECK = 'check',
  BANK_TRANSFER = 'bank_transfer',
  MOBILE_MONEY = 'mobile_money',
  ONLINE = 'online',
  CARD = 'card',
}

export enum PaymentMethod {
  CASH = 'cash',
  CHECK = 'check',
  BANK_TRANSFER = 'bank_transfer',
  CARD = 'card',
  MOBILE_MONEY = 'mobile_money',
  EFT = 'eft',
}

export enum RestrictionType {
  UNRESTRICTED = 'unrestricted',
  TEMPORARILY_RESTRICTED = 'temporarily_restricted',
  PERMANENTLY_RESTRICTED = 'permanently_restricted',
}

// ============================================================================
// CHART OF ACCOUNTS
// ============================================================================

export interface ChartOfAccount {
  id: string;
  church_id: string;
  account_code: string;
  account_name: string;
  account_type: AccountType;
  account_subtype: AccountSubtype;
  parent_account_id?: string;
  is_active: boolean;
  is_system_account: boolean;
  allow_manual_entry: boolean;
  balance_sheet_category?: string;
  income_statement_category?: string;
  cash_flow_category?: string;
  opening_balance: number;
  opening_balance_date?: Date;
  current_balance: number;
  last_transaction_date?: Date;
  restriction_type?: RestrictionType;
  description?: string;
  notes?: string;
  created_at: Date;
  updated_at: Date;
  created_by?: string;
  updated_by?: string;
}

export interface CreateAccountInput {
  church_id: string;
  account_code: string;
  account_name: string;
  account_type: AccountType;
  account_subtype: AccountSubtype;
  parent_account_id?: string;
  allow_manual_entry?: boolean;
  opening_balance?: number;
  opening_balance_date?: Date;
  description?: string;
}

// ============================================================================
// GENERAL LEDGER
// ============================================================================

export interface JournalEntry {
  id: string;
  church_id: string;
  entry_number: string;
  entry_date: Date;
  posting_date?: Date;
  source_type: SourceType;
  source_reference_id?: string;
  status: JournalStatus;
  fiscal_year: number;
  fiscal_period: number;
  description: string;
  reference?: string;
  total_debit: number;
  total_credit: number;
  requires_approval: boolean;
  approved_by?: string;
  approved_at?: Date;
  approval_notes?: string;
  is_reversal: boolean;
  reverses_entry_id?: string;
  reversed_by_entry_id?: string;
  reversal_reason?: string;
  attachment_urls?: string[];
  created_at: Date;
  updated_at: Date;
  created_by?: string;
  posted_by?: string;
  lines?: JournalEntryLine[];
}

export interface JournalEntryLine {
  id: string;
  journal_entry_id: string;
  church_id: string;
  line_number: number;
  account_id: string;
  debit_amount: number;
  credit_amount: number;
  description?: string;
  department_id?: string;
  project_id?: string;
  fund_id?: string;
  is_reconciled: boolean;
  reconciliation_id?: string;
  created_at: Date;
}

export interface CreateJournalEntryInput {
  church_id: string;
  entry_date: Date;
  source_type: SourceType;
  source_reference_id?: string;
  description: string;
  reference?: string;
  lines: CreateJournalLineInput[];
  requires_approval?: boolean;
  attachment_urls?: string[];
}

export interface CreateJournalLineInput {
  account_id: string;
  debit_amount: number;
  credit_amount: number;
  description?: string;
  fund_id?: string;
}

// ============================================================================
// EXPENSES
// ============================================================================

export interface Expense {
  id: string;
  church_id: string;
  expense_number: string;
  expense_date: Date;
  vendor_id?: string;
  vendor_name?: string;
  description: string;
  expense_category?: string;
  amount: number;
  vat_amount: number;
  total_amount: number;
  currency: string;
  invoice_number?: string;
  invoice_date?: Date;
  due_date?: Date;
  expense_account_id: string;
  fund_id?: string;
  department_id?: string;
  project_id?: string;
  payment_method?: string;
  payment_reference?: string;
  payment_date?: Date;
  status: ExpenseStatus;
  submitted_by?: string;
  submitted_at?: Date;
  requires_approval: boolean;
  approval_level: number;
  current_approver_id?: string;
  journal_entry_id?: string;
  is_posted_to_ledger: boolean;
  attachment_urls?: string[];
  notes?: string;
  rejection_reason?: string;
  created_at: Date;
  updated_at: Date;
  created_by?: string;
  approved_by?: string;
  approved_at?: Date;
  rejected_by?: string;
  rejected_at?: Date;
  amount_paid: number;
  balance_due: number;
  is_fully_paid: boolean;
  payments?: ExpensePayment[];
}

export interface ExpensePayment {
  id: string;
  expense_id: string;
  payment_number: string;
  payment_date: Date;
  payment_method: PaymentMethod;
  payment_reference?: string;
  payment_amount: number;
  notes?: string;
  journal_entry_id?: string;
  created_at: Date;
  created_by?: string;
}

export interface CreateExpenseInput {
  church_id: string;
  expense_date: Date;
  vendor_id?: string;
  vendor_name?: string;
  description: string;
  expense_category?: string;
  amount: number;
  vat_amount?: number;
  invoice_number?: string;
  invoice_date?: Date;
  due_date?: Date;
  expense_account_id: string;
  fund_id?: string;
  attachment_urls?: string[];
  notes?: string;
}

export interface ApproveExpenseInput {
  expense_id: string;
  approver_id: string;
  notes?: string;
}

export interface PayExpenseInput {
  expense_id: string;
  payment_date: Date;
  payment_method: PaymentMethod;
  payment_reference?: string;
  payment_amount: number;
  notes?: string;
}

// ============================================================================
// CONTRIBUTIONS
// ============================================================================

export interface Contribution {
  id: string;
  church_id: string;
  member_id?: string;
  contributor_name?: string;
  contributor_email?: string;
  contributor_phone?: string;
  is_anonymous: boolean;
  contribution_date: Date;
  contribution_type: ContributionType;
  contribution_method: ContributionMethod;
  amount: number;
  currency: string;
  fund_id?: string;
  designation?: string;
  reference_number?: string;
  payment_gateway?: string;
  gateway_transaction_id?: string;
  is_in_kind: boolean;
  in_kind_description?: string;
  fair_market_value?: number;
  is_tax_deductible: boolean;
  tax_receipt_number?: string;
  tax_receipt_issued_date?: Date;
  pledge_id?: string;
  journal_entry_id?: string;
  is_posted_to_ledger: boolean;
  status: string;
  notes?: string;
  created_at: Date;
  updated_at: Date;
  created_by?: string;
  recorded_by?: string;
}

export interface CreateContributionInput {
  church_id: string;
  member_id?: string;
  contributor_name?: string;
  is_anonymous?: boolean;
  contribution_date: Date;
  contribution_type: ContributionType;
  contribution_method: ContributionMethod;
  amount: number;
  fund_id?: string;
  designation?: string;
  reference_number?: string;
  is_tax_deductible?: boolean;
  pledge_id?: string;
  notes?: string;
}

// ============================================================================
// VENDORS
// ============================================================================

export interface Vendor {
  id: string;
  church_id: string;
  vendor_code: string;
  vendor_name: string;
  vendor_type?: string;
  vendor_category?: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  tax_id?: string;
  is_vat_vendor: boolean;
  bank_name?: string;
  bank_account_number?: string;
  payment_terms_days: number;
  default_expense_account_id?: string;
  payables_account_id?: string;
  is_active: boolean;
  current_balance: number;
  notes?: string;
  created_at: Date;
  updated_at: Date;
}

// ============================================================================
// FUNDS
// ============================================================================

export interface Fund {
  id: string;
  church_id: string;
  fund_code: string;
  fund_name: string;
  restriction_type: RestrictionType;
  description?: string;
  purpose?: string;
  donor_name?: string;
  restriction_start_date?: Date;
  restriction_end_date?: Date;
  restriction_conditions?: string;
  current_balance: number;
  asset_account_id?: string;
  equity_account_id?: string;
  is_active: boolean;
  is_endowment: boolean;
  created_at: Date;
  updated_at: Date;
  created_by?: string;
}

// ============================================================================
// REPORTS
// ============================================================================

export interface TrialBalanceRow {
  account_code: string;
  account_name: string;
  account_type: AccountType;
  debit_balance: number;
  credit_balance: number;
}

export interface TrialBalance {
  church_id: string;
  as_of_date: Date;
  rows: TrialBalanceRow[];
  total_debits: number;
  total_credits: number;
  difference: number;
  is_balanced: boolean;
}

export interface BalanceSheetRow {
  account_code: string;
  account_name: string;
  amount: number;
  level: number;
  is_total: boolean;
}

export interface BalanceSheet {
  church_id: string;
  as_of_date: Date;
  assets: BalanceSheetRow[];
  liabilities: BalanceSheetRow[];
  equity: BalanceSheetRow[];
  total_assets: number;
  total_liabilities: number;
  total_equity: number;
  is_balanced: boolean;
}

export interface IncomeStatementRow {
  account_code: string;
  account_name: string;
  amount: number;
  level: number;
  is_total: boolean;
}

export interface IncomeStatement {
  church_id: string;
  start_date: Date;
  end_date: Date;
  revenue: IncomeStatementRow[];
  expenses: IncomeStatementRow[];
  total_revenue: number;
  total_expenses: number;
  net_income: number;
}

// ============================================================================
// SERVICE RESPONSES
// ============================================================================

export interface ServiceResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

export interface ValidationError {
  field: string;
  message: string;
}

export interface BusinessRuleViolation {
  rule: string;
  message: string;
  suggestion?: string;
}

// ============================================================================
// BUDGETS
// ============================================================================

export enum BudgetStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  CLOSED = 'closed',
}

export interface Budget {
  id: string;
  church_id: string;
  budget_name: string;
  budget_year: number;
  start_date: Date;
  end_date: Date;
  status: BudgetStatus;
  total_budgeted_amount: number;
  total_actual_amount: number;
  notes?: string;
  created_by?: string;
  approved_by?: string;
  approved_at?: Date;
  created_at: Date;
  updated_at: Date;
  line_items?: BudgetLineItem[];
}

export interface BudgetLineItem {
  id: string;
  budget_id: string;
  account_id: string;
  budgeted_amount: number;
  actual_amount: number;
  variance: number; // actual - budgeted
  variance_percent: number; // (variance / budgeted) * 100
  notes?: string;
  created_at: Date;
  updated_at: Date;
  monthly_breakdown?: BudgetMonthlyBreakdown[];
}

export interface BudgetMonthlyBreakdown {
  id: string;
  budget_line_item_id: string;
  month_number: number; // 1-12
  budgeted_amount: number;
  actual_amount: number;
  created_at: Date;
  updated_at: Date;
}

export interface CreateBudgetInput {
  church_id: string;
  budget_name: string;
  budget_year: number;
  start_date: Date;
  end_date: Date;
  notes?: string;
  line_items: CreateBudgetLineItemInput[];
}

export interface CreateBudgetLineItemInput {
  account_id: string;
  budgeted_amount: number;
  notes?: string;
  monthly_breakdown?: {
    month_number: number;
    budgeted_amount: number;
  }[];
}

export interface UpdateBudgetInput {
  budget_id: string;
  budget_name?: string;
  notes?: string;
  line_items?: UpdateBudgetLineItemInput[];
}

export interface UpdateBudgetLineItemInput {
  id?: string; // If updating existing
  account_id: string;
  budgeted_amount: number;
  notes?: string;
}

export interface BudgetVsActual {
  budget_id: string;
  church_id: string;
  budget_name: string;
  budget_year: number;
  line_item_id: string;
  account_id: string;
  account_code: string;
  account_name: string;
  account_type: AccountType;
  budgeted_amount: number;
  actual_amount: number;
  variance: number;
  variance_percent: number;
  status: 'No Budget' | 'Over Budget' | 'Under Budget' | 'On Track';
}

export interface BudgetSummary {
  budget_id: string;
  budget_name: string;
  budget_year: number;
  total_budgeted: number;
  total_actual: number;
  total_variance: number;
  variance_percent: number;
  revenue_budgeted: number;
  revenue_actual: number;
  expense_budgeted: number;
  expense_actual: number;
  accounts_over_budget: number;
  accounts_under_budget: number;
  accounts_on_track: number;
}

// ============================================================================
// BANK RECONCILIATION
// ============================================================================

export enum BankReconciliationStatus {
  IN_PROGRESS = 'in_progress',
  RECONCILED = 'reconciled',
  DISCREPANCY = 'discrepancy',
}

export interface BankReconciliation {
  id: string;
  church_id: string;
  bank_account_id: string;
  reconciliation_date: Date;
  statement_date: Date;
  statement_beginning_balance: number;
  statement_ending_balance: number;
  book_balance: number;
  reconciled_balance?: number;
  difference: number;
  is_reconciled: boolean;
  status: BankReconciliationStatus;
  reconciled_by?: string;
  reconciled_at?: Date;
  notes?: string;
  created_at: Date;
  updated_at: Date;
  items?: BankReconciliationItem[];
}

export interface BankReconciliationItem {
  id: string;
  reconciliation_id: string;
  transaction_id?: string; // Links to general_ledger_lines
  transaction_date: Date;
  description: string;
  debit_amount: number;
  credit_amount: number;
  is_cleared: boolean;
  cleared_date?: Date;
  is_bank_side: boolean; // TRUE if on bank statement but not in books
  notes?: string;
  created_at: Date;
  updated_at: Date;
}

export interface BankStatementImport {
  id: string;
  reconciliation_id?: string;
  church_id: string;
  bank_account_id: string;
  import_date: Date;
  statement_date: Date;
  file_name?: string;
  total_debits: number;
  total_credits: number;
  transaction_count: number;
  matched_count: number;
  unmatched_count: number;
  imported_by?: string;
  created_at: Date;
  transactions?: BankStatementTransaction[];
}

export interface BankStatementTransaction {
  id: string;
  import_id: string;
  transaction_date: Date;
  description: string;
  reference_number?: string;
  debit_amount: number;
  credit_amount: number;
  balance?: number;
  is_matched: boolean;
  matched_gl_line_id?: string;
  created_at: Date;
}

export interface CreateBankReconciliationInput {
  church_id: string;
  bank_account_id: string;
  reconciliation_date: Date;
  statement_date: Date;
  statement_beginning_balance: number;
  statement_ending_balance: number;
  book_balance: number;
  notes?: string;
}

export interface AddReconciliationItemInput {
  reconciliation_id: string;
  transaction_id?: string;
  transaction_date: Date;
  description: string;
  debit_amount: number;
  credit_amount: number;
  is_bank_side?: boolean;
  notes?: string;
}

export interface MarkItemClearedInput {
  item_id: string;
  cleared_date: Date;
}

export interface MatchBankTransactionInput {
  bank_transaction_id: string;
  gl_line_id: string;
}

export interface ImportBankStatementInput {
  church_id: string;
  bank_account_id: string;
  statement_date: Date;
  file_name?: string;
  transactions: {
    transaction_date: Date;
    description: string;
    reference_number?: string;
    debit_amount: number;
    credit_amount: number;
    balance?: number;
  }[];
}

export interface BankReconciliationSummary {
  id: string;
  church_id: string;
  reconciliation_date: Date;
  statement_date: Date;
  statement_ending_balance: number;
  book_balance: number;
  reconciled_balance?: number;
  difference: number;
  is_reconciled: boolean;
  status: BankReconciliationStatus;
  account_code: string;
  account_name: string;
  total_items: number;
  cleared_items: number;
  uncleared_items: number;
  cleared_debits: number;
  cleared_credits: number;
}
